import { FunctionComponent, useState, useEffect, useRef } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import PersonIcon from '@mui/icons-material/Person';
import MedicationIcon from '@mui/icons-material/Medication';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/Check';
import SearchIcon from '@mui/icons-material/Search';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CircularProgress from '@mui/material/CircularProgress';
import { useSavedItems, SavedItem } from '../../../contexts/SavedItemsContext';
import { savedApi, ApiError } from '../../../services/api';
import styles from './CreateSavedListModal.module.css';

interface CreateSavedListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onListCreated?: (listId: string) => void;
}

interface SelectedItem {
  id: string;
  type: 'provider' | 'medication' | 'procedure' | 'care-plan';
  title: string;
  subtitle?: string;
}

const CreateSavedListModal: FunctionComponent<CreateSavedListModalProps> = ({
  isOpen,
  onClose,
  onListCreated
}) => {
  const { savedItems, saveItem } = useSavedItems();
  const [listName, setListName] = useState('');
  const [listDescription, setListDescription] = useState('');
  const [selectedItems, setSelectedItems] = useState<SelectedItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'all' | 'provider' | 'medication' | 'procedure' | 'care-plan'>('all');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Reset form when modal opens
      setListName('');
      setListDescription('');
      setSelectedItems([]);
      setSearchQuery('');
      setActiveTab('all');
      setError(null);
      setIsCreating(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const filteredItems = savedItems.filter(item => {
    if (item.type === 'list') return false; // Don't show lists in selection
    
    const matchesSearch = searchQuery === '' || 
      item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (item.subtitle && item.subtitle.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesTab = activeTab === 'all' || item.type === activeTab;
    
    return matchesSearch && matchesTab;
  });

  const handleItemToggle = (item: SavedItem) => {
    const isSelected = selectedItems.some(selected => 
      selected.id === item.id && selected.type === item.type
    );

    if (isSelected) {
      setSelectedItems(selectedItems.filter(selected => 
        !(selected.id === item.id && selected.type === item.type)
      ));
    } else {
      setSelectedItems([...selectedItems, {
        id: item.id,
        type: item.type as any,
        title: item.title,
        subtitle: item.subtitle
      }]);
    }
  };

  const handleCreateList = async () => {
    if (!listName.trim()) return;

    setIsCreating(true);
    setError(null);

    try {
      // Prepare the data for the API
      const listData = {
        name: listName.trim(),
        description: listDescription.trim(),
        categories: {
          providers: selectedItems.filter(item => item.type === 'provider').map(item => {
            const savedItem = savedItems.find(saved => saved.id === item.id && saved.type === item.type);
            return savedItem?.data || { id: item.id, name: item.title };
          }),
          procedures: selectedItems.filter(item => item.type === 'procedure').map(item => {
            const savedItem = savedItems.find(saved => saved.id === item.id && saved.type === item.type);
            return savedItem?.data || { id: item.id, name: item.title };
          }),
          medications: selectedItems.filter(item => item.type === 'medication').map(item => {
            const savedItem = savedItems.find(saved => saved.id === item.id && saved.type === item.type);
            return savedItem?.data || { id: item.id, name: item.title };
          }),
          articles: [] // Placeholder for future article support
        }
      };

      // Make API call to create the list using the centralized API service
      const result = await savedApi.createList(listData);

      if (result) {
        // Save the list locally as well
        const newList = {
          id: result.id,
          type: 'list' as const,
          title: result.name || result.title,
          subtitle: `${selectedItems.length} items`,
          savedAt: new Date(),
          data: result
        };
        
        saveItem(newList);
        onListCreated?.(result.id);
        onClose();
      }
    } catch (error) {
      console.error('Error creating list:', error);
      if (error instanceof ApiError) {
        setError(error.message);
      } else {
        setError('An error occurred while creating the list. Please try again.');
      }
    } finally {
      setIsCreating(false);
    }
  };

  const getItemIcon = (type: string) => {
    switch (type) {
      case 'provider':
        return <PersonIcon className={styles.itemIcon} />;
      case 'medication':
        return <MedicationIcon className={styles.itemIcon} />;
      case 'procedure':
        return <MedicalServicesIcon className={styles.itemIcon} />;
      case 'care-plan':
        return <AutoAwesomeIcon className={styles.itemIcon} />;
      default:
        return null;
    }
  };

  const tabs = [
    { id: 'all', label: 'All', count: savedItems.filter(item => item.type !== 'list').length },
    { id: 'provider', label: 'Providers', count: savedItems.filter(item => item.type === 'provider').length },
    { id: 'medication', label: 'Medications', count: savedItems.filter(item => item.type === 'medication').length },
    { id: 'procedure', label: 'Procedures', count: savedItems.filter(item => item.type === 'procedure').length },
    { id: 'care-plan', label: 'Care Plans', count: savedItems.filter(item => item.type === 'care-plan').length }
  ];

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div 
        ref={modalRef}
        className={styles.modal} 
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <div className={styles.headerTitle}>
            <FolderIcon className={styles.titleIcon} />
            <h2>Create New List</h2>
          </div>
          <button className={styles.closeButton} onClick={onClose}>
            <CloseIcon />
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.formSection}>
            <label className={styles.label}>List Name *</label>
            <input
              type="text"
              className={styles.input}
              placeholder="e.g., Heart Health Resources"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              maxLength={100}
            />
          </div>

          <div className={styles.formSection}>
            <label className={styles.label}>Description</label>
            <textarea
              className={styles.textarea}
              placeholder="Add a description for your list..."
              value={listDescription}
              onChange={(e) => setListDescription(e.target.value)}
              rows={3}
              maxLength={500}
            />
          </div>

          <div className={styles.itemsSection}>
            <div className={styles.sectionHeader}>
              <label className={styles.label}>Add Items to List (Optional)</label>
              <span className={styles.selectedCount}>
                {selectedItems.length} selected
              </span>
            </div>

            <div className={styles.searchBar}>
              <SearchIcon className={styles.searchIcon} />
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search saved items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className={styles.tabs}>
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`${styles.tab} ${activeTab === tab.id ? styles.activeTab : ''}`}
                  onClick={() => setActiveTab(tab.id as any)}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>

            <div className={styles.itemsList}>
              {filteredItems.length === 0 ? (
                <div className={styles.emptyState}>
                  <p>No items found</p>
                  {searchQuery && (
                    <button 
                      className={styles.clearSearchButton}
                      onClick={() => setSearchQuery('')}
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                filteredItems.map(item => {
                  const isSelected = selectedItems.some(selected => 
                    selected.id === item.id && selected.type === item.type
                  );
                  
                  return (
                    <div
                      key={`${item.type}-${item.id}`}
                      className={`${styles.selectableItem} ${isSelected ? styles.selected : ''}`}
                      onClick={() => handleItemToggle(item)}
                    >
                      <div className={styles.itemContent}>
                        <div className={styles.itemIconContainer}>
                          {getItemIcon(item.type)}
                        </div>
                        <div className={styles.itemInfo}>
                          <h4 className={styles.itemTitle}>{item.title}</h4>
                          {item.subtitle && (
                            <p className={styles.itemSubtitle}>{item.subtitle}</p>
                          )}
                        </div>
                      </div>
                      <div className={styles.checkbox}>
                        {isSelected && <CheckIcon />}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {error && (
          <div className={styles.errorMessage}>
            <ErrorOutlineIcon className={styles.errorIcon} />
            <span>{error}</span>
          </div>
        )}

        <div className={styles.footer}>
          <button className={styles.cancelButton} onClick={onClose} disabled={isCreating}>
            Cancel
          </button>
          <button 
            className={styles.createButton}
            onClick={handleCreateList}
            disabled={!listName.trim() || isCreating}
          >
            {isCreating ? (
              <>
                <CircularProgress size={16} className={styles.spinner} />
                Creating...
              </>
            ) : (
              selectedItems.length > 0 ? `Create List (${selectedItems.length})` : 'Create Empty List'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSavedListModal;